// YouTube mode sidebar tree (clone of ../components/ListTree.tsx).
//
//   ▼ queue (100)
//       Title of the video
//       channel · 12:34
//   ▸ watched (48)          ← rows show the rating right-aligned in accent
//   ▸ ignore (7)
//
// Selection lives in URL search params (?list=queue&video=<id>). Expanded
// state persists to localStorage under "cinedb.youtube.tree.expanded".

import { useState } from "react";
import { useSearchParams } from "react-router-dom";

import { fmtDuration, useYtListItems, useYtLists, YT_LIST_NAMES, type YtListItem, type YtListName } from "./api";
import {
  type YtFilterState,
  ytFilterLabel,
  ytFilterOptions,
  YT_SORT_FIELD_LABEL,
  type YtSortState,
  ytViewItems,
} from "./sort";

import "../components/ListTree.css";
import "./youtube.css";

const EXPANDED_STORAGE_KEY = "cinedb.youtube.tree.expanded";

function loadExpanded(): Record<string, boolean> {
  if (typeof window === "undefined") return { queue: true };
  try {
    const raw = window.localStorage.getItem(EXPANDED_STORAGE_KEY);
    if (raw === null) return { queue: true };
    return JSON.parse(raw) as Record<string, boolean>;
  } catch {
    return { queue: true };
  }
}

function saveExpanded(map: Record<string, boolean>): void {
  try {
    window.localStorage.setItem(EXPANDED_STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* ignore */
  }
}

export function YoutubeListTree({
  sort,
  filter,
  onCycleField,
  onToggleDir,
  onSetFilter,
}: {
  sort: YtSortState;
  filter: YtFilterState | null;
  onCycleField: () => void;
  onToggleDir: () => void;
  onSetFilter: (value: string | null) => void;
}) {
  const { data: counts, error, isLoading } = useYtLists();
  const [expanded, setExpanded] = useState<Record<string, boolean>>(() => loadExpanded());
  const [filterOpen, setFilterOpen] = useState(false);

  function toggle(name: YtListName) {
    setExpanded((prev) => {
      const next = { ...prev, [name]: !prev[name] };
      saveExpanded(next);
      return next;
    });
  }

  const toolbar = (
    <div className="tree-sort">
      <div className="tree-filter">
        <button
          type="button"
          className={"tree-filter__btn" + (filter ? " tree-filter__btn--active" : "")}
          onClick={() => setFilterOpen((o) => !o)}
          title="filter the lists by the current sort category"
        >
          ▾ {filter ? ytFilterLabel(filter) : "filter"}
        </button>
        {filterOpen && (
          <>
            <div className="tree-filter__overlay" onClick={() => setFilterOpen(false)} />
            <div className="tree-filter__menu" role="menu">
              <button
                type="button"
                className={"tree-filter__item" + (!filter ? " tree-filter__item--active" : "")}
                onClick={() => {
                  onSetFilter(null);
                  setFilterOpen(false);
                }}
              >
                all
              </button>
              {ytFilterOptions(sort.field).map((o) => (
                <button
                  key={o.value}
                  type="button"
                  className={
                    "tree-filter__item" +
                    (filter?.value === o.value ? " tree-filter__item--active" : "")
                  }
                  onClick={() => {
                    onSetFilter(o.value);
                    setFilterOpen(false);
                  }}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
      <button
        type="button"
        className="tree-sort__field"
        onClick={onCycleField}
        title="sort field — click to cycle: A–Z / channel / published / duration / watches / rating"
      >
        sort: {YT_SORT_FIELD_LABEL[sort.field]}
      </button>
      <button
        type="button"
        className="tree-sort__dir"
        onClick={onToggleDir}
        title={sort.dir === "asc" ? "ascending — click for descending" : "descending — click for ascending"}
        aria-label={sort.dir === "asc" ? "ascending" : "descending"}
      >
        {sort.dir === "asc" ? "↑" : "↓"}
      </button>
    </div>
  );

  if (error) {
    return (
      <>
        {toolbar}
        <div className="tree-empty">couldn't load lists</div>
      </>
    );
  }
  if (isLoading || !counts) {
    return (
      <>
        {toolbar}
        <div className="tree-empty">loading…</div>
      </>
    );
  }

  return (
    <>
      {toolbar}
      <ul className="tree" role="tree">
        {YT_LIST_NAMES.map((name) => (
          <ListNode
            key={name}
            name={name}
            count={counts[name]?.count ?? 0}
            expanded={!!expanded[name]}
            onToggle={() => toggle(name)}
            sort={sort}
            filter={filter}
          />
        ))}
      </ul>
    </>
  );
}

function ListNode({
  name,
  count,
  expanded,
  onToggle,
  sort,
  filter,
}: {
  name: YtListName;
  count: number;
  expanded: boolean;
  onToggle: () => void;
  sort: YtSortState;
  filter: YtFilterState | null;
}) {
  const { data, error, isLoading } = useYtListItems(expanded ? name : null);
  const [params, setParams] = useSearchParams();
  const selectedList = params.get("list");
  const selectedVideo = params.get("video");
  const items = data ? ytViewItems(data.items, sort, filter) : [];

  function selectVideo(item: YtListItem) {
    const next = new URLSearchParams(params);
    next.set("list", name);
    next.set("video", item.video_id);
    setParams(next, { replace: true });
  }

  return (
    <li className="tree__list" role="treeitem" aria-expanded={expanded}>
      <div
        className="tree__list-row"
        onClick={onToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle();
          }
        }}
      >
        <span className="tree__chevron" aria-hidden>
          {expanded ? "▼" : "▶"}
        </span>
        <span className="tree__list-name">{name}</span>
        <span className="tree__list-count">({count})</span>
      </div>

      {expanded && (
        <ul className="tree__titles" role="group">
          {isLoading && <li className="tree__empty-titles">loading…</li>}
          {error && <li className="tree__empty-titles">couldn't load {name}</li>}
          {data && items.length === 0 && (
            <li className="tree__empty-titles">
              {filter && data.items.length > 0 ? "no matches for this filter" : "nothing here"}
            </li>
          )}
          {items.map((item) => {
            const selected = selectedList === name && selectedVideo === item.video_id;
            return (
              <VideoRow
                key={item.video_id}
                item={item}
                list={name}
                selected={selected}
                onSelect={() => selectVideo(item)}
              />
            );
          })}
        </ul>
      )}
    </li>
  );
}

function VideoRow({
  item,
  list,
  selected,
  onSelect,
}: {
  item: YtListItem;
  list: YtListName;
  selected: boolean;
  onSelect: () => void;
}) {
  const sub = [item.channel, fmtDuration(item.duration_seconds)].filter(Boolean).join(" · ");
  return (
    <li
      className={`tree__title${selected ? " tree__row--selected" : ""}`}
      role="treeitem"
      aria-selected={selected}
      onClick={onSelect}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect();
        }
      }}
    >
      <div className="tree__title-row">
        <span className="tree__title-name">
          {item.title}
          {sub && <span className="yt-tree__sub">{sub}</span>}
        </span>
        {list === "watched" && item.rating != null && (
          <span className="tree__title-rating">{item.rating}</span>
        )}
      </div>
    </li>
  );
}
